import { CompRef, DocumentServicesObject } from '@wix/document-services-types';
import { ComponentType } from '@wix/ambassador-devcenter-components-v1-component/types';
import { ExperimentsAPI } from '@wix/editorx-core-api';
import {
  IProcessManifestProps,
  IProcessPanelProps,
  ManifestLifecycle,
} from './lifecycle';
import { ClientSpecMapService } from './sharedServices';
import {
  IVirtualManifestMetadata,
  ManifestOverridesResolver,
  ManifestStore,
  PanelStore,
  VirtualManifestsStore,
  EditorReactComponentExtensionType,
  ManifestComponentExtensionType,
  ProviderManifestStore,
} from './stores';

import { HostAPI, IWixCLIIntegrationAPI } from './types';
import { PlatformPrivateEventEmitter } from '@wix/public-editor-platform-events';
import { ComponentsPlatformService } from './componentsPlatformServices';

import {
  AbstractComponentFlow,
  CustomElementComponentFlow,
  EditorReactComponentFlow,
  OOIComponentFlow,
  SystemComponentFlow,
} from './flows';
import {
  createEditorPlatformManifestServicesError,
  EditorPlatformManifestServicesErrorCode,
} from './errors';
import { IVirtualManifestBuilderProps, ProviderComponentApi } from './api';

import { OperationSource } from './panorama';
import {
  ChangeType,
  IChangeCallback,
  ListenerPriority,
  ManifestChangeDetection,
} from './ManifestChangeDetection';
import {
  DynamicOverridesMiddleware,
  InternalSystemContainerMiddleware,
  PluginOverrideMiddleware,
  RefElementDisplayMiddleware,
} from './middlewares';
import type { IManifestServicesBridge } from './types';
import { ManifestServiceFlags } from './ManifestServiceFlags';
import type { VirtualElementProvider } from './api/providers/VirtualElementProvider';
import { ManifestServiceStats } from './ManifestServiceStats';
import { EditorEnvironment, PlatformManifestService } from './bootstrap';
import { EditorContextProviderFlow } from './flows/EditorContextProviderFlow';
import { AbstractVirtualManifestBuilder } from './builders';
import { DependencyToken } from './dependencies';
import { RuntimeLabelsService } from './runtime-labels';
import type {
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-document-logic-api';

export class ManifestServicesHost {
  #readyPromiseResolve = () => {};

  readonly #readyPromise = new Promise<void>((resolve) => {
    this.#readyPromiseResolve = () => resolve();
  });

  readonly #clientSpecMapService: ClientSpecMapService;

  readonly #manifestLifecycle: ManifestLifecycle;

  readonly #panelStore: PanelStore;
  readonly #platformManifestService: PlatformManifestService;
  readonly #providerManifestStore: ProviderManifestStore;
  readonly #virtualManifestStore: VirtualManifestsStore;

  readonly #componentsService: ComponentsPlatformService;

  readonly editorPointerAPI: EditorPointerServiceAPI;

  // @ts-expect-error defined in the constructor
  readonly #flows: Record<
    ManifestComponentExtensionType,
    AbstractComponentFlow<ManifestStore | ProviderManifestStore>
  > = {};

  public readonly virtualElementProvider: VirtualElementProvider;

  public readonly manifestChangeDetection: ManifestChangeDetection;

  readonly #manifestServiceFlags: ManifestServiceFlags;
  public manifestServiceStats = new ManifestServiceStats();

  private pendingInvalidateCompTypes = new Map<
    string,
    ManifestComponentExtensionType
  >();
  private pendingInvalidateTimeoutId: ReturnType<typeof setTimeout> | null =
    null;

  private readonly DEBOUNCE_DELAY_MS = 16.7; // ~1 frame at 60fps

  readonly #providerComponentApi: ProviderComponentApi;

  #storeMap: {
    [key in ManifestComponentExtensionType]: key extends EditorReactComponentExtensionType
      ? ManifestStore
      : ProviderManifestStore;
  };

  constructor(
    private dm: DocumentServicesObject,
    private readonly hostAPI: HostAPI,
    private readonly experimentsAPI: ExperimentsAPI,
    private readonly events: PlatformPrivateEventEmitter,
    private readonly editorPlatformPackagesBridge: Partial<IManifestServicesBridge>,
    private readonly wixCLIIntegrationAPI: IWixCLIIntegrationAPI,
  ) {
    this.#manifestServiceFlags = ManifestServiceFlags.create(
      this.experimentsAPI,
    );

    this.editorPointerAPI = this.editorPlatformPackagesBridge.EditorPointerAPI!;

    this.manifestChangeDetection = new ManifestChangeDetection(
      this.editorPointerAPI,
      this.dm.transactions.isRunning.bind(this.dm),
      this.dm.registerToSiteChanged.bind(this.dm) as (
        cb: (tags: string[]) => void,
      ) => void,
    );

    this.#clientSpecMapService = new ClientSpecMapService(
      this.dm,
      this.editorPointerAPI,
    );

    this.#providerManifestStore = new ProviderManifestStore();

    this.#manifestLifecycle = new ManifestLifecycle(
      this.dm,
      this.manifestChangeDetection,
      this.editorPointerAPI,
      this.#manifestServiceFlags,
    );

    this.#panelStore = new PanelStore(this.#clientSpecMapService);

    this.#platformManifestService = new PlatformManifestService(
      this.#manifestServiceFlags,
      this.manifestChangeDetection,
      this.manifestServiceStats,
    );

    new EditorEnvironment(
      this.editorPointerAPI,
      this.dm,
      this.hostAPI,
      this.#clientSpecMapService,
      this.editorPlatformPackagesBridge.EditorPlatformPanoramaClientAPI!,
      this.editorPlatformPackagesBridge.ProvidersServiceAPI,
      this.editorPlatformPackagesBridge.DataBindingServiceAPI,
    ).setDependenciesMethods(this.#platformManifestService);

    this.#platformManifestService.dependenciesContributor.lock();

    this.#componentsService = new ComponentsPlatformService(
      this.dm,
      this.hostAPI,
      this.#platformManifestService.virtualElementProvider,
      this.#platformManifestService.manifestStore,
      this.#panelStore,
      this.#manifestLifecycle,
      this.editorPointerAPI,
      this.manifestChangeDetection,
      this.manifestServiceStats,
      this.experimentsAPI,
      this.#clientSpecMapService,
    );

    this.#providerComponentApi = new ProviderComponentApi(
      this.editorPlatformPackagesBridge.ProvidersServiceAPI,
      this.editorPlatformPackagesBridge.DataBindingServiceAPI,
    );

    this.#storeMap = {
      [ManifestComponentExtensionType.BUILDER]:
        this.#platformManifestService.manifestStore,
      [ManifestComponentExtensionType.OOI]:
        this.#platformManifestService.manifestStore,
      [ManifestComponentExtensionType.CUSTOM_ELEMENT]:
        this.#platformManifestService.manifestStore,
      [ManifestComponentExtensionType.SYSTEM]:
        this.#platformManifestService.manifestStore,
      [ManifestComponentExtensionType.UNKNOWN]:
        this.#platformManifestService.manifestStore,
      [ManifestComponentExtensionType.CONTEXT_PROVIDER]:
        this.#providerManifestStore,
    } as const;

    this.#platformManifestService.manifestStore.withManifestOverridesResolver(
      new ManifestOverridesResolver(
        this.editorPointerAPI,
        this.#platformManifestService.manifestStore,
        this.#platformManifestService.virtualElementProvider,
        this.#manifestServiceFlags,
      )
        .withMiddleware(
          new DynamicOverridesMiddleware(
            this.editorPointerAPI,
            this.dm,
            this.manifestChangeDetection,
            this.#manifestServiceFlags,
            this.manifestServiceStats,
          ),
        )
        .withMiddleware(new RefElementDisplayMiddleware(this.editorPointerAPI))
        .withMiddleware(
          new InternalSystemContainerMiddleware(
            this.editorPointerAPI,
            this.#componentsService.containers,
          ),
        )
        .withMiddleware(
          new PluginOverrideMiddleware(
            this.editorPointerAPI,
            this.#componentsService.containers,
          ),
        ),
    );

    this.virtualElementProvider =
      this.#platformManifestService.virtualElementProvider;

    this.#virtualManifestStore = new VirtualManifestsStore(
      this.editorPointerAPI,
      this.#platformManifestService.virtualElementProvider,
      this.manifestChangeDetection,
      this.manifestServiceStats,
    );

    const runtimeLabelsService = new RuntimeLabelsService(
      this.#platformManifestService.dependenciesContributor,
      this.#manifestServiceFlags.isRuntimeManifestLabelsEnabled,
    );

    const builderFlow = new EditorReactComponentFlow(
      this.dm,
      this.editorPointerAPI,
      this.#storeMap[ManifestComponentExtensionType.BUILDER],
      this.#panelStore,
      this.wixCLIIntegrationAPI,
      this.#manifestServiceFlags,
      {},
      runtimeLabelsService,
    );

    this.#flows[builderFlow.type] = builderFlow;

    [OOIComponentFlow, CustomElementComponentFlow].forEach((Factory) => {
      const instance: AbstractComponentFlow<ManifestStore> = new Factory(
        this.dm,
        this.editorPointerAPI,
        this.#storeMap[Factory.extensionType],
        this.#panelStore,
        this.wixCLIIntegrationAPI,
        this.#manifestServiceFlags,
      );

      this.#flows[instance.type] = instance;
    });

    this.#flows[ManifestComponentExtensionType.CONTEXT_PROVIDER] =
      new EditorContextProviderFlow(
        this.dm,
        this.editorPointerAPI,
        this.#storeMap[ManifestComponentExtensionType.CONTEXT_PROVIDER],
        this.#panelStore,
        this.wixCLIIntegrationAPI,
        this.#manifestServiceFlags,
      );
  }

  #assertReady() {
    if (!this.editorPointerAPI) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.Internal,
        'editor pointer API is not available, manifest services are not functional',
      );
    }
  }

  async #invalidateAllFlowCaches() {
    await Promise.allSettled(
      Object.values(this.#flows).map((flow) => flow.invalidateCache()),
    );
  }

  start() {
    this.#assertReady();
    this.#manifestLifecycle.onClientSpecMapRefresh(() =>
      this.#invalidateAllFlowCaches(),
    );

    this.#manifestLifecycle.onManifestLoad(async (props) => {
      try {
        await this.#readyPromise;

        const extensionType = this.#getManifestComponentExtensionType(
          props.appDataComponent.type,
        );

        const store = this.#storeMap[extensionType];

        const componentFlow = this.#flows[extensionType];

        if (!componentFlow || !store) {
          return;
        }

        if (props.force) {
          await componentFlow.invalidateCache();
        }

        const compTypes = props.compTypes;

        const isSkipped = compTypes.some((compType) => {
          const isAlreadyExist = store.has(compType);

          if (!isAlreadyExist) {
            return false;
          }

          const shouldBeUpdated =
            !props.isPendingApplication && store.isPendingManifest(compType);

          return !shouldBeUpdated;
        });

        if (isSkipped && !props.force) {
          return;
        }

        await this.#processManifest(extensionType, props);

        if (
          props.force && // why do we need force here?
          props.compTypes.length &&
          extensionType !== ManifestComponentExtensionType.UNKNOWN
        ) {
          this.notifyManifestChange(props.compTypes, extensionType);
        }
      } catch (e) {
        throw e;
      }
    });

    this.#manifestLifecycle.onPanelLoad(async (x) => {
      try {
        await this.#readyPromise;
        await this.#processComponentPanel(x);
      } catch (e) {
        throw e;
      }
    });

    void this.#loadInitialData();
    this.#manifestLifecycle.startListeners();
  }

  async #loadInitialData() {
    await Promise.all([this.#processSystemComponents()]);

    this.#readyPromiseResolve();
  }

  #getManifestComponentExtensionType(
    componentType: string,
  ): ManifestComponentExtensionType {
    switch (componentType) {
      case ComponentType.EDITOR_REACT_COMPONENT:
        return ManifestComponentExtensionType.BUILDER;

      case ComponentType.EDITOR_CONTEXT_PROVIDER:
        return ManifestComponentExtensionType.CONTEXT_PROVIDER;

      case ComponentType.CUSTOM_ELEMENT_WIDGET:
        return ManifestComponentExtensionType.CUSTOM_ELEMENT;

      case ComponentType.PAGE_OUT_OF_IFRAME:
      case ComponentType.PAGE:
      case ComponentType.WIDGET:
      case ComponentType.WIDGET_OUT_OF_IFRAME:
        return ManifestComponentExtensionType.OOI;

      default:
        return ManifestComponentExtensionType.UNKNOWN;
    }
  }

  async #processManifest(
    extensionType: ManifestComponentExtensionType,
    props: IProcessManifestProps,
  ) {
    // if (this.#manifestStore.has(props.compType) && !props.force) {
    //   return;
    // }

    switch (extensionType) {
      case ManifestComponentExtensionType.BUILDER: {
        await this.#flows[ManifestComponentExtensionType.BUILDER].loadManifest(
          props,
        );

        break;
      }

      case ManifestComponentExtensionType.CONTEXT_PROVIDER: {
        await this.#flows[
          ManifestComponentExtensionType.CONTEXT_PROVIDER
        ].loadManifest(props);

        break;
      }

      case ManifestComponentExtensionType.CUSTOM_ELEMENT: {
        await this.#flows[
          ManifestComponentExtensionType.CUSTOM_ELEMENT
        ].loadManifest(props);

        break;
      }

      case ManifestComponentExtensionType.OOI: {
        // change ManifestComponentExtensionType to LegacyComponentFlow
        await this.#flows[ManifestComponentExtensionType.OOI].loadManifest(
          props,
        );

        break;
      }
    }
  }

  async #processSystemComponents() {
    const systemFlow = new SystemComponentFlow(
      this.dm,
      this.editorPointerAPI,
      this.#platformManifestService.manifestStore,
      this.#panelStore,
      this.wixCLIIntegrationAPI,
      this.#manifestServiceFlags,
    );

    const compTypes = Object.entries(
      this.#platformManifestService.manifestStore.getAll(),
    )
      .filter(
        ([, value]) =>
          value.extensionType === ManifestComponentExtensionType.SYSTEM,
      )
      .map(([key]) => key);

    const results = await Promise.allSettled(
      compTypes.map((compType) =>
        systemFlow.loadPanelsByCompType({ compType }),
      ),
    );

    const failedCompTypes = results
      .map((result, idx) =>
        result.status === 'rejected' ? compTypes[idx] : null,
      )
      .filter(Boolean);

    if (failedCompTypes.length > 0) {
      console.error(
        `Failed to load panels for compTypes: ${failedCompTypes.join(', ')}`,
      );
    }
  }

  async #processComponentPanel(props: IProcessPanelProps) {
    switch (props.appDataComponent.type) {
      case ComponentType.EDITOR_REACT_COMPONENT: {
        await this.#flows[ManifestComponentExtensionType.BUILDER].loadPanels(
          props,
        );

        break;
      }

      case ComponentType.CUSTOM_ELEMENT_WIDGET: {
        await this.#flows[
          ManifestComponentExtensionType.CUSTOM_ELEMENT
        ].loadPanels(props);
        break;
      }

      case ComponentType.PAGE_OUT_OF_IFRAME:
      case ComponentType.WIDGET_OUT_OF_IFRAME: {
        await this.#flows[ManifestComponentExtensionType.OOI].loadPanels(props);

        break;
      }
    }
  }

  buildManifestAPI(props: IVirtualManifestBuilderProps) {
    return this.virtualElementProvider.buildVirtualElement(props).getAPI();
  }

  getContextualManifest(virtualManifestId: string) {
    return this.#virtualManifestStore.getContextualVirtualManifest(
      virtualManifestId,
    );
  }

  getContextualMetadata(virtualManifestId: string) {
    return this.#virtualManifestStore.getContextualVirtualManifestMetadata(
      virtualManifestId,
    );
  }

  getContextualManifestAPI(
    virtualManifestId: string,
    childPath: number[],
    source?: OperationSource,
  ) {
    return this.#virtualManifestStore.getContextualVirtualManifestAPI(
      virtualManifestId,
      childPath,
      source,
    );
  }

  buildContextualManifest(
    props: IVirtualManifestBuilderProps,
    metadata?: IVirtualManifestMetadata,
  ) {
    return this.#virtualManifestStore.buildContextualVirtualManifest(
      props,
      metadata,
    );
  }

  buildContextualProviderManifest(
    contextType: string,
    props: IVirtualManifestBuilderProps,
    metadata?: IVirtualManifestMetadata,
  ) {
    const providerManifest =
      this.#providerManifestStore.getProviderVirtualManifestByContextType(
        contextType,
      );

    const editorPointer = Array.isArray(props.editorPointers)
      ? props.editorPointers[0]
      : props.editorPointers;

    if (!editorPointer) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.EditorPointerIsNotResolved,
        'editorPointer is required to build a contextual manifest with context',
      );
    }

    props.contextBuilder = (builder: AbstractVirtualManifestBuilder) => {
      return builder
        .withoutComponents()
        .withEditorElement(providerManifest)
        .contributeExternalAPI(
          DependencyToken.UNKNOWN_COMPONENT_DATA_GET,
          () => () =>
            this.#providerComponentApi.getData(editorPointer, contextType),
        )
        .contributeExternalAPI(
          DependencyToken.UNKNOWN_COMPONENT_DATA_UPDATE,
          () => (_ep, data) =>
            this.#providerComponentApi.setData(
              editorPointer,
              contextType,
              data,
            ),
        )
        .contributeExternalAPI(
          DependencyToken.BINDINGS_BIND,
          () => (_target, bindings) =>
            this.#providerComponentApi.bind(
              { editorPointer, contextType },
              bindings,
            ),
        )
        .contributeExternalAPI(
          DependencyToken.BINDINGS_SET_BINDING,
          () => (_target, bindings) =>
            this.#providerComponentApi.setBinding(
              { editorPointer, contextType },
              bindings,
            ),
        )
        .contributeExternalAPI(
          DependencyToken.BINDINGS_LIST,
          () => (_target, options?) =>
            this.#providerComponentApi.listBindings(
              { editorPointer, contextType },
              options,
            ),
        )
        .contributeExternalAPI(
          DependencyToken.BINDINGS_GET_BINDING,
          () => (_target, options?) =>
            this.#providerComponentApi.getBinding(
              { editorPointer, contextType },
              options,
            ),
        )
        .contributeExternalAPI(
          DependencyToken.BINDINGS_UNBIND,
          () => (_target, bindingProps) =>
            this.#providerComponentApi.unbind(
              { editorPointer, contextType },
              bindingProps,
            ),
        )
        .contributeExternalAPI(
          DependencyToken.BINDINGS_FIND_BINDING_CANDIDATES,
          () => (options) =>
            this.#providerComponentApi.findBindingCandidates({
              ...options,
              target: { editorPointer, contextType },
            }),
        )
        .contributeExternalAPI(
          DependencyToken.PROVIDERS_LIST,
          () => (options) =>
            this.#providerComponentApi.list(
              { editorPointer, contextType },
              options,
            ),
        );
    };

    props.cacheNamespace = undefined;
    props.editorPointers = undefined;

    return this.buildContextualManifest(props, {
      ...metadata,
      boundObjectRef: { editorPointer, contextType },
    });
  }

  subscribeOnComponentChange(
    cb: IChangeCallback,
    options: {
      editorPointers?: EditorPointer<CompRef>[];
    } = {},
  ) {
    return this.manifestChangeDetection.subscribe(cb, {
      ...options,
      priority: ListenerPriority.External,
    });
  }

  /**
   * for debug purposes
   */
  getComponentsService() {
    this.#assertReady();
    return this.#componentsService;
  }

  /**
   * for debug purposes
   */
  getBuilderManifests() {
    this.#assertReady();
    return this.#platformManifestService.manifestStore.getAll();
  }

  /**
   * for debug purposes
   */
  getBuilderPanels() {
    this.#assertReady();
    return this.#panelStore.getAll();
  }

  /**
   * for debug purposes
   */
  getManifestByEditorPointer(editorPointer: EditorPointer<CompRef>) {
    this.#assertReady();
    return this.#platformManifestService.manifestStore.getResolvedEditorElement(
      editorPointer,
    );
  }

  private notifyManifestChange(
    compTypes: string[],
    extensionType: ManifestComponentExtensionType,
  ) {
    if (compTypes.length === 0) {
      return;
    }

    compTypes.forEach((compType) => {
      this.pendingInvalidateCompTypes.set(compType, extensionType);
    });

    this.scheduleInvalidation();
  }

  private scheduleInvalidation() {
    if (this.pendingInvalidateTimeoutId !== null) {
      clearTimeout(this.pendingInvalidateTimeoutId);
    }

    this.pendingInvalidateTimeoutId = setTimeout(() => {
      this.flushPendingInvalidations();
    }, this.DEBOUNCE_DELAY_MS);
  }

  private flushPendingInvalidations() {
    if (this.pendingInvalidateCompTypes.size === 0) {
      return;
    }

    const compTypeMap = new Map(this.pendingInvalidateCompTypes);
    this.pendingInvalidateCompTypes.clear();
    this.pendingInvalidateTimeoutId = null;

    const components = this.dm.components.getAllComponents();

    components.forEach((compRef) => {
      const editorPointer = this.editorPointerAPI.buildEditorPointer(compRef);
      const currentCompType =
        this.#clientSpecMapService.getCompType(editorPointer);
      const extensionType = compTypeMap.get(currentCompType);

      if (extensionType !== undefined) {
        this.manifestChangeDetection.notify({
          editorPointer,
          extensionType,
          type: ChangeType.Manifest,
          value: null,
        });
      }
    });
  }
}
